import "./serviceGraphics.css";

const Tick = () => (
  <svg viewBox="0 0 16 16">
    <path d="M3.5 8.5 L6.5 11.5 L12.5 4.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Cross = () => (
  <svg viewBox="0 0 16 16">
    <path d="M4.5 4.5 L11.5 11.5 M11.5 4.5 L4.5 11.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

type Stop = { label: string; state?: "fail" | "off" | "broken" | "end" };
type Preset = {
  title: string;
  before: Stop[];
  beforeNote: string;
  after: Stop[];
  afterNote: string;
  chip: string;
  chipSub: string;
};

/** Before/after stories for fix jobs on the Work page. Generic wording only. */
export type FixKind = "cart" | "language";

export const FIX_PRESETS: Record<FixKind, Preset> = {
  cart: {
    title: "shopify / cart · before → after",
    before: [
      { label: "Product" },
      { label: "Add to cart", state: "broken" },
      { label: "Cart", state: "fail" },
      { label: "Checkout", state: "off" },
      { label: "Order", state: "off" },
    ],
    beforeNote: "Shoppers got stuck at the cart",
    after: [
      { label: "Product" },
      { label: "Add to cart" },
      { label: "Cart" },
      { label: "Checkout" },
      { label: "Order placed", state: "end" },
    ],
    afterNote: "Cart → checkout works end to end",
    chip: "Cart issue · Resolved",
    chipSub: "Shopify store",
  },
  language: {
    title: "website / language · before → after",
    before: [
      { label: "Home · FR", state: "broken" },
      { label: "Page 2 · EN", state: "fail" },
      { label: "Page 3 · EN", state: "off" },
      { label: "Page 4 · EN", state: "off" },
    ],
    beforeNote: "Picked français — the next page reset to the default",
    after: [
      { label: "Home · FR" },
      { label: "Page 2 · FR" },
      { label: "Page 3 · FR" },
      { label: "Page 4 · FR", state: "end" },
    ],
    afterNote: "EN or FR — the choice follows you to every page",
    chip: "Language bug · Resolved",
    chipSub: "Bilingual EN / FR",
  },
};

const Flow = ({ stops }: { stops: Stop[] }) => (
  <ol className="gfx-flow__line">
    {stops.map((s) => (
      <li key={s.label} className={"gfx-flow__stop" + (s.state ? ` gfx-flow__stop--${s.state}` : "")}>
        <span className="gfx-flow__dot">{s.state === "fail" ? <Cross /> : s.state === "off" ? null : <Tick />}</span>
        {s.label}
      </li>
    ))}
  </ol>
);

/**
 * Before/after of a user flow, for Work entries where we fixed something
 * rather than built the site and don't show the client's own pages.
 * Same drawing system as ServiceGraphic; decorative.
 */
const FixGraphic = ({ kind }: { kind: FixKind }) => {
  const p = FIX_PRESETS[kind];
  return (
    <div className="is-gfx gfx--cartfix" aria-hidden="true">
      <div className="gfx-win">
        <div className="gfx-bar">
          <div className="gfx-lights">
            <i />
            <i />
            <i />
          </div>
          <span className="gfx-title">{p.title}</span>
        </div>
        <div className="gfx-flow">
          <div className="gfx-flow__row">
            <span className="gfx-flow__tag">BEFORE</span>
            <Flow stops={p.before} />
            <span className="gfx-flow__note">{p.beforeNote}</span>
          </div>
          <div className="gfx-flow__row gfx-flow__row--after">
            <span className="gfx-flow__tag">AFTER</span>
            <Flow stops={p.after} />
            <span className="gfx-flow__note">{p.afterNote}</span>
          </div>
        </div>
      </div>
      <div className="gfx-chip gfx-resolved">
        <span className="gfx-check">
          <Tick />
        </span>
        <span>
          <b>{p.chip}</b>
          <span>{p.chipSub}</span>
        </span>
      </div>
    </div>
  );
};

export default FixGraphic;
